import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  CalendarToday as CalendarIcon,
  Close,
} from '@mui/icons-material';

import apiService from '../services/api';
import dayjs from 'dayjs';
import FloorEditDialog from '../components/modals/EscCalenderEditDialog';
import { EscCalenderSingleValue, TableColumn } from '../types';
import { toast } from 'react-toastify';
import DataTable from '../components/dataTable';

const EscCalender: React.FC = () => {
  const [calender, setcalender] = useState<EscCalenderSingleValue[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] =
    useState<EscCalenderSingleValue | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [calenderToDelete, setCalenderToDelete] =
    useState<EscCalenderSingleValue | null>(null);

  const fetchEscCalender = async () => {
    setLoading(true);
    try {
      const calenderResponse = await apiService.getEscCalender();
      setcalender(calenderResponse.data || []);
      setTotal(calenderResponse.total || 0);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch session');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEscCalender();
  }, []);

  const handleEditEvent = (event: EscCalenderSingleValue) => {
    setEditingEvent(event);
    setEditDialogOpen(true);
  };

  const handleDeleteEvent = (event: EscCalenderSingleValue) => {
    setCalenderToDelete(event);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!calenderToDelete) return;

    try {
      const response = await apiService.deleteEscCalender(calenderToDelete.id);
      toast.success('ESC Calendar deleted successfully');
      setDeleteDialogOpen(false);
      setCalenderToDelete(null);
      fetchEscCalender();
    } catch (err: any) {
      const apiError =
        err?.response?.data?.message ||
        err?.message ||
        'Something went wrong. Please try again.';
      setError(apiError);
      toast.error(apiError);
    }
  };

  const handleSessionSaved = () => {
    fetchEscCalender();
    setEditDialogOpen(false);
    setEditingEvent(null);
  };

  const handlePageChange = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const formatDate = (date: Date | string) => {
    return new Date(date + 'T00:00:00').toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatTime = (time: string | null | undefined) => {
    if (!time) return '-';
    return dayjs(time, 'HH:mm:ss').format('hh:mm A');
  };

  const columns: TableColumn<EscCalenderSingleValue>[] = [
    {
      label: 'Title',
      keys: ['title', 'description'],
      renderGroup: values => {
        const [title, description] = values as [string, string];
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <span style={{ fontSize: '0.875rem' }}>{title}</span>
            <span
              style={{
                fontSize: '0.75rem',
                marginTop: '2px',
                color: '#6b7280',
                maxWidth: '250px', // adjust as needed
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {description}
            </span>
          </Box>
        );
      },
    },

    {
      label: 'Date Range',
      keys: ['startDate', 'endDate'],
      renderGroup: values => {
        const [start, end] = values as [string | Date, string | Date];

        const format = (d: string | Date) =>
          new Date(d).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          });

        return (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <span style={{ fontSize: '0.875rem' }}>{format(start)}</span>
            <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
              to {format(end)}
            </span>
          </Box>
        );
      },
    },
    {
      label: 'Time',
      keys: ['startTime', 'endTime'],
      renderGroup: values => {
        const [start, end] = values as [string, string];
        return (
          <Typography sx={{ fontSize: '0.875rem', color: '#111827' }}>
            {formatTime(start)} - {formatTime(end)}
          </Typography>
        );
      },
    },
    { label: 'Venue', key: 'venue' },
    { label: 'Scheme', key: 'scheme' },
    {
      label: 'Apply Link',
      key: 'applyLink',
      renderGroup: values => {
        const [value] = values as [string];

        if (!value) return '-';

        return (
          <a
            href={value}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: '#2563eb',
              textDecoration: 'underline',
              fontSize: '0.875rem',
            }}
          >
            Open Link
          </a>
        );
      },
    },
  ];

  return (
    <Box sx={{ p: 3, bgcolor: '#f9fafb', minHeight: '100vh' }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3,
        }}
      >
        <Typography
          sx={{ fontSize: '1.875rem', fontWeight: 700, color: '#111827' }}
        >
          ESC Calender Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          sx={{
            bgcolor: '#3b82f6',
            '&:hover': { bgcolor: '#2563eb' },
            textTransform: 'none',
            fontWeight: 500,
            px: 2,
            py: 1,
          }}
          onClick={() => setEditDialogOpen(true)}
        >
          Create ESC calendar
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Main Content Card */}
      <Card
        sx={{
          boxShadow:
            '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
          border: '1px solid #e5e7eb',
          borderRadius: 2,
        }}
      >
        {/* Table */}
        <DataTable<EscCalenderSingleValue>
          columns={columns}
          data={calender}
          actions={row => [
            {
              icon: <EditIcon fontSize="small" />,
              onClick: () => handleEditEvent(row),
            },
            {
              icon: <DeleteIcon fontSize="small" />,
              onClick: () => handleDeleteEvent(row),
            },
          ]}
        />

        {/* Pagination */}
        {calender.length > 0 && (
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[10, 25, 50, 100]}
            sx={{
              borderTop: '1px solid #e5e7eb',
              '& .MuiTablePagination-toolbar': {
                px: 3,
              },
            }}
          />
        )}
      </Card>

      {/* Edit Dialog */}
      <FloorEditDialog
        open={editDialogOpen}
        calender={editingEvent}
        onClose={() => {
          setEditDialogOpen(false);
          setEditingEvent(null);
        }}
        onSave={handleSessionSaved}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box>
            <Typography sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
              {' '}
              Confirm Delete{' '}
            </Typography>
          </Box>
          <IconButton
            onClick={() => setDeleteDialogOpen(false)}
            disabled={loading}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete event "{calenderToDelete?.title}
            "? This action cannot be undone and will also remove all associated
            registrations and sessions.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            sx={{
              backgroundColor: 'rgba(211, 47, 47, 0.08)',
              color: '#d32f2f',
              fontWeight: 500,
              '&:hover': {
                backgroundColor: 'rgba(211, 47, 47, 0.15)',
              },
            }}
          >
            Cancel
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EscCalender;
